import {
  MaterialPriceList,
  ServiceContainer,
  UpdateMonitorSnapshotTimesReq,
  UpdateServiceTimePerMonitorRequest,
} from "@/services/backend/qpm/calculations/types/calculation";
import {
  CalculationServiceEntryType,
  CalculationServiceType,
} from "@/services/backend/qpm/calculations/types/calculation-enum";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { CalculationServiceEntryUtil } from "@/services/backend/qpm/calculations/lib/calculation-service-entry-util";
import {
  TimeNumberInput,
  TimeUnit,
} from "@/shared/components/inputs/time-number-input";
import { FloatNumberInput } from "@/shared/components/domain/qpm/inputs/number-input";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { H4 } from "@/shared/components/typography";
import { useMemo, useState } from "react";
import {
  useQpmCalcServiceGroupServiceTimePerMonitorPostMutation,
  useQpmCalcUpdateMonitorSnapshotTimesMutation,
} from "@/services/backend/qpm/calculations/api/calculation-api";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { MonitorSnapDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { MonitorSelectSearchComponent } from "@/routes/processes/[processId]/qpm/_components/monitor-select-search-component";
import { Button } from "@/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Info, Trash2 } from "lucide-react";

const tooltipContentClassName =
  "bg-background text-foreground max-w-sm border text-left text-xs";

export type ServiceTimeComponentProps = {
  calculationId: string;
  materialCost: Record<MonitorType, MaterialPriceList>;
  monitorSnaps: Record<string, MonitorSnapDTO>;
  serviceGroup: ServiceContainer;
  serviceTypeKey: CalculationServiceType;
};

export function ServiceTimeComponent({
  calculationId,
  materialCost,
  monitorSnaps,
  serviceGroup,
  serviceTypeKey,
}: ServiceTimeComponentProps) {
  const entryTypes =
    serviceTypeKey === CalculationServiceType.Maintenance
      ? [
          CalculationServiceEntryType.CheckPoint,
          CalculationServiceEntryType.CheckPointVisual,
          CalculationServiceEntryType.CheckPointIoT,
          CalculationServiceEntryType.CheckPointSCB,
          CalculationServiceEntryType.FIV,
          CalculationServiceEntryType.ALoRa,
        ]
      : [
          CalculationServiceEntryType.CheckPoint,
          CalculationServiceEntryType.CheckPointVisual,
          CalculationServiceEntryType.CheckPointSCB,
          CalculationServiceEntryType.FIV,
          CalculationServiceEntryType.ALoRa,
        ];

  return (
    <div className="flex flex-col gap-4 p-4">
      <H4 className="font-bold">{t("Servicezeiten für Monitore")}</H4>
      <Table className="border">
        <TableHeader>
          <TableRow className="bg-secondary">
            <TableHead className="text-left">{t("Service Art")}</TableHead>
            <TableHead className="text-right">
              {t("Arbeitszeit pro Kontrollstation")}
            </TableHead>
            <TableHead className="text-right">
              {t("Wert pro Kontrollstation in Euro")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entryTypes.map((etype) => (
            <TableRow key={etype}>
              <TableCell>
                {CalculationServiceEntryUtil.toLongString(
                  etype,
                  serviceTypeKey,
                )}
              </TableCell>
              <TableCell>
                <ServiceTimePerMonitorComponent
                  calculationId={calculationId}
                  serviceTypeKey={serviceTypeKey}
                  serviceTimePerMonitor={
                    serviceGroup.serviceParameters[etype]?.duration || 0
                  }
                  entryType={etype}
                />
              </TableCell>
              <TableCell className="text-end">
                <FloatNumberInput
                  value={
                    serviceGroup.serviceParameters[etype]?.costPerMonitor || 0
                  }
                  disabled
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <MonitorServiceTimeOverrideTable
        calculationId={calculationId}
        materialCost={materialCost}
        monitorSnaps={monitorSnaps}
        serviceGroup={serviceGroup}
        serviceTypeKey={serviceTypeKey}
      />
    </div>
  );
}

type MonitorServiceTimeOverrideTableProps = {
  calculationId: string;
  materialCost: Record<MonitorType, MaterialPriceList>;
  monitorSnaps: Record<string, MonitorSnapDTO>;
  serviceGroup: ServiceContainer;
  serviceTypeKey: CalculationServiceType;
};

function MonitorServiceTimeOverrideTable({
  calculationId,
  materialCost,
  monitorSnaps,
  serviceGroup,
  serviceTypeKey,
}: MonitorServiceTimeOverrideTableProps) {
  const defaultOverrideTime = 60 * 1e9;
  const selectedMonitorIds = useMemo(() => {
    const ids = new Set<string>();

    Object.values(materialCost ?? {}).forEach((priceList) => {
      priceList.positions.forEach((position) => {
        if (position.amount > 0) {
          ids.add(position.monitorId);
        }
      });
    });

    if (ids.size === 0) {
      serviceGroup.serviceGroups.forEach((group) => {
        group.entries.forEach((entry) => {
          entry.monitors.forEach((monitor) => {
            ids.add(monitor.monitorId);
          });
        });
      });
    }

    return ids;
  }, [materialCost, serviceGroup]);

  const monitors = useMemo(
    () =>
      Object.values(monitorSnaps ?? {})
        .filter((monitor) => selectedMonitorIds.has(monitor.sourceId))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [monitorSnaps, selectedMonitorIds],
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedTimes, setSelectedTimes] = useState<Record<string, number>>(
    {},
  );
  const available = monitors.filter((m) => !selectedIds.includes(m.sourceId));
  const [updateTimes] = useQpmCalcUpdateMonitorSnapshotTimesMutation();

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <H4 className="font-bold">{t("Individuelle Monitorzeiten")}</H4>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={t("Hinweis zu individuellen Monitorzeiten")}
                >
                  <Info className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent className={tooltipContentClassName}>
                <p>
                  {t(
                    "Diese Zeiten haben Vorrang in der Kalkulation. Wenn eine Zeit gesetzt ist, wird sie für diesen Monitor verwendet.",
                  )}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <MonitorSelectSearchComponent
          monitorList={available}
          onMonitorSelect={(monitorId) => {
            if (!monitorId) return;
            const monitor = monitorSnaps[monitorId];
            if (!monitor) return;
            const nextRequest: UpdateMonitorSnapshotTimesReq = {
              calculationId,
              monitorId,
              install:
                serviceTypeKey === CalculationServiceType.Installation
                  ? defaultOverrideTime
                  : (monitor.times.install ?? 0),
              maintain:
                serviceTypeKey === CalculationServiceType.Maintenance
                  ? defaultOverrideTime
                  : (monitor.times.maintain ?? 0),
            };
            setSelectedIds((current) =>
              current.includes(monitorId) ? current : [...current, monitorId],
            );
            setSelectedTimes((current) => ({
              ...current,
              [monitorId]: defaultOverrideTime,
            }));
            updateTimes(nextRequest);
          }}
        />
      </div>
      {selectedIds.length > 0 ? (
        <Table className="border">
          <TableHeader>
            <TableRow className="bg-secondary">
              <TableHead>{t("Monitor")}</TableHead>
              <TableHead className="text-right">
                {serviceTypeKey === CalculationServiceType.Installation
                  ? t("Installationszeit")
                  : t("Servicezeit")}
              </TableHead>
              <TableHead className="w-16 text-right">{t("Aktion")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {selectedIds.map((monitorId) => {
              const monitor = monitorSnaps[monitorId];
              if (!monitor) return null;
              return (
                <MonitorServiceTimeOverrideRow
                  key={monitorId}
                  calculationId={calculationId}
                  initialTime={selectedTimes[monitorId] ?? 0}
                  monitor={monitor}
                  onRemove={() =>
                    setSelectedIds((current) =>
                      current.filter((id) => id !== monitorId),
                    )
                  }
                  serviceTypeKey={serviceTypeKey}
                />
              );
            })}
          </TableBody>
        </Table>
      ) : null}
    </div>
  );
}

function MonitorServiceTimeOverrideRow({
  calculationId,
  initialTime,
  monitor,
  onRemove,
  serviceTypeKey,
}: {
  calculationId: string;
  initialTime: number;
  monitor: MonitorSnapDTO;
  onRemove: () => void;
  serviceTypeKey: CalculationServiceType;
}) {
  const field =
    serviceTypeKey === CalculationServiceType.Installation
      ? "install"
      : "maintain";
  const [time, setTime] = useState(initialTime || monitor.times[field] || 0);
  const [request, setRequest] = useState<UpdateMonitorSnapshotTimesReq>({
    calculationId,
    monitorId: monitor.sourceId,
    install:
      field === "install"
        ? initialTime || monitor.times.install || 0
        : monitor.times.install || 0,
    maintain:
      field === "maintain"
        ? initialTime || monitor.times.maintain || 0
        : monitor.times.maintain || 0,
  });
  const [updateTimes, { isLoading, error, reset, isSuccess }] =
    useQpmCalcUpdateMonitorSnapshotTimesMutation();

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    updateTimes,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
  );

  const removeOverride = () => {
    const nextRequest = {
      ...request,
      [field]: 0,
    };
    setTime(0);
    setRequest(nextRequest);
    updateTimes(nextRequest);
    onRemove();
  };

  return (
    <TableRow>
      <TableCell>{monitor.name}</TableCell>
      <TableCell>
        <TimeNumberInput
          className="justify-self-end"
          value={time || 0}
          onChange={(val) => {
            const next = val < 0 ? 0 : val;
            setTime(next);
            setRequest({
              ...request,
              [field]: next,
            });
          }}
          timeUnit={TimeUnit.Minutes}
          max={10_000}
          disabled={isLoading}
        />
      </TableCell>
      <TableCell className="text-right">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={removeOverride}
                disabled={isLoading}
                aria-label={t("Individuelle Monitorzeit entfernen")}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className={tooltipContentClassName}>
              <p>{t("Entfernen")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
    </TableRow>
  );
}

export type ServiceTimePerMonitorComponentProps = {
  calculationId: string;
  serviceTypeKey: CalculationServiceType;
  entryType: CalculationServiceEntryType;
  serviceTimePerMonitor: number;
};

export function ServiceTimePerMonitorComponent({
  calculationId,
  serviceTypeKey,
  entryType,
  serviceTimePerMonitor,
}: ServiceTimePerMonitorComponentProps) {
  const [serviceTime, setServiceTime] = useState(serviceTimePerMonitor);
  const [request, setRequest] = useState<UpdateServiceTimePerMonitorRequest>({
    calculationId,
    serviceTypeKey,
    entryType,
    serviceTimePerMonitor,
  });

  const [setServiceTimePerMonitor, { isLoading, error, reset, isSuccess }] =
    useQpmCalcServiceGroupServiceTimePerMonitorPostMutation();

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    setServiceTimePerMonitor,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
  );

  return (
    <TimeNumberInput
      className="justify-self-end"
      value={serviceTime || 0}
      onChange={(val) => {
        const time = val < 0 ? 0 : val;
        setServiceTime(time);
        setRequest({
          ...request,
          serviceTimePerMonitor: time,
        });
      }}
      timeUnit={TimeUnit.Minutes}
      max={10_000}
      disabled={isLoading}
    />
  );
}
